import React, { useState } from "react";
import { Eye, Upload, Trash2, Sparkles } from "lucide-react";
import { useToast } from "../context/ToastContext";
import DeleteConfirmModal from "../components/DeleteConfirmModal";


const WidgetPreview = () => {
  const { showToast } = useToast();
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleImageUpload = (file) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showToast("Please upload an image file", "error");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showToast("Image size should be less than 5MB", "warning");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64Image = e.target.result;
      setUploadedImage(base64Image);
      setImagePreview(base64Image);
      sessionStorage.setItem("widget_preview_background", base64Image);
      showToast("Background image updated!", "success");
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    handleImageUpload(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    handleImageUpload(file);
  };

  const handlePreview = () => {
    window.open("/widget-preview/display", "_blank");
  };

  const handleRemoveImage = () => {
    setIsDeleteModalOpen(true);
  };

  const confirmRemoveImage = () => {
    setIsDeleting(true);
    setUploadedImage(null);
    setImagePreview(null);
    sessionStorage.removeItem("widget_preview_background");
    showToast("Background image removed", "info");
    setIsDeleteModalOpen(false);
    setIsDeleting(false);
  };

  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn">
      <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4">
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">
                Vision Simulation
              </h1>
            </div>
            <p className="text-xs text-zinc-500 mt-0.5 sm:mt-1">
              Test the architectural harmony of your assistant within its native environment.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-5 sm:gap-6 items-start">
          <div className="md:col-span-3">
            <div className="bg-white rounded-xl p-6 sm:p-8 border border-zinc-200 shadow-2xs">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-bold text-zinc-900 text-xs uppercase tracking-wider">Contextual Simulation</h3>
                  <p className="text-[11px] font-medium text-zinc-400 mt-0.5">Upload a screenshot of your target page</p>
                </div>
              </div>

              {!uploadedImage ? (
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onClick={() =>
                    document.getElementById("image-upload-input").click()
                  }
                  className="group relative rounded-xl p-8 sm:p-12 border-2 border-dashed transition-all cursor-pointer text-center border-zinc-200 bg-zinc-50/50 hover:border-zinc-300 hover:bg-zinc-50 flex flex-col items-center justify-center"
                >
                  <Upload size={22} className="text-zinc-500 mb-2.5 transition-colors group-hover:text-zinc-800" />
                  <h4 className="text-xs font-bold text-zinc-900 mb-1">
                    Drop your website screenshot
                  </h4>
                  <p className="text-[11px] text-zinc-500 max-w-xs mx-auto leading-relaxed">
                    Upload a 1920x1080 capture of your site to see exactly how the widget integrates.
                  </p>
                  <div className="flex flex-wrap justify-center gap-1.5 mt-4">
                     {["PNG", "JPG", "MAX 5MB"].map((ext) => (
                        <span key={ext} className="px-2 py-0.5 bg-white rounded border border-zinc-200 text-[10px] font-semibold text-zinc-500 uppercase">
                          {ext}
                        </span>
                      ))}
                  </div>
                  <input
                    id="image-upload-input"
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="relative rounded-xl overflow-hidden border border-zinc-200 shadow-2xs group">
                    <img
                      src={imagePreview}
                      alt="Preview background"
                      className="w-full max-h-[400px] object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                    <button
                      onClick={handleRemoveImage}
                      className="absolute top-4 right-4 p-2 bg-white/90 backdrop-blur-md text-red-500 rounded-lg border border-red-100 hover:bg-red-50 hover:border-red-200 transition-all shadow-sm"
                      title="Remove background"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  
                  <button
                    onClick={handlePreview}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-zinc-900 text-white rounded-xl hover:bg-zinc-800 transition-all font-semibold text-xs shadow-xs"
                  >
                    <Eye size={14} />
                    Launch Live Preview
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="md:col-span-2">
            {!uploadedImage && (
              <div className="bg-zinc-900 rounded-xl p-6 sm:p-8 border border-zinc-800 shadow-2xl relative overflow-hidden group">
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles size={16} className="text-zinc-400" />
                    <h3 className="text-white font-bold text-xs uppercase tracking-wider font-semibold">
                      Quick Preview
                    </h3>
                  </div>
                  <p className="text-zinc-400 text-xs leading-relaxed mb-6">
                    Don't have a screenshot ready? Preview the widget on a clean, minimal canvas to verify your brand colors and interactions.
                  </p>
                </div>
                <button
                  onClick={handlePreview}
                  className="relative z-10 w-full px-4 py-3 bg-white text-zinc-900 rounded-xl hover:bg-zinc-50 transition-all font-semibold text-xs shadow-xs flex items-center justify-center gap-2"
                >
                  <Eye size={14} />
                  Standard Preview
                </button>
                <div className="absolute -right-8 -bottom-8 opacity-[0.03] pointer-events-none group-hover:scale-110 transition-transform duration-700">
                  <Eye size={160} className="text-white" strokeWidth={1} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <DeleteConfirmModal 
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmRemoveImage}
        isLoading={isDeleting}
        confirmText="Remove Image"
        title="Un-Simulation Context?"
        message="This will decouple the background environment from the vision simulation. Are you prepared to proceed?"
      />
    </div>
  );
};

export default WidgetPreview;
